import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  Paper,
  Snackbar,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import StarIcon from '@mui/icons-material/Star';
import { Link as RouterLink } from 'react-router-dom';
import { Plan, ProrationPreview, Subscription } from '../types';
import { fetchPlans, fetchAppConfig, fetchComparisonFeatures, ComparisonFeatureRow } from '../services/publicApi';
import { fetchMySubscription, fetchProrationPreview } from '../services/subscriptionService';
import { fetchPaymentGateways, GatewayId, initiateWithFallback, openCashfreeCheckout, verifyPayment } from '../services/paymentService';
import { useAuth } from '../contexts/AuthContext';

declare global {
  interface Window { Razorpay?: any; }
}

interface FeatureRow {
  label: string;
  key: string;
  format?: (value: any, plan: Plan) => React.ReactNode;
}

const fixText = (s: string) =>
  s
    .replace(/\bride contacts\b/gi, 'rides')
    .replace(/\bcontact unlocks\b/gi, 'ride unlocks')
    .replace(/\bunlocked contacts\b/gi, 'unlocked rides')
    .replace(/\bverified ride contacts\b/gi, 'verified rides')
    .replace(/\bcontacts per month\b/gi, 'rides per month')
    .replace(/\bcontact details\b/gi, 'ride details')
    .replace(/\blead contacts\b/gi, 'rides')
    .replace(/\bride leads\b/gi, 'rides')
    .replace(/\bleads\b/gi, 'rides');


const Plans: React.FC = () => {
  const { token } = useAuth();
  const [plans, setPlans] = useState<Plan[]>([]);
  const [subscription, setSubscription] = useState<Subscription | null>(null);
  const [gateways, setGateways] = useState<Array<{ id: GatewayId; mode: 'test' | 'live' }>>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [workingId, setWorkingId] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [signupFreeCredits, setSignupFreeCredits] = useState<number>(5);
  const [comparisonFeatures, setComparisonFeatures] = useState<ComparisonFeatureRow[]>([]);
  const [proration, setProration] = useState<ProrationPreview | null>(null);
  const [confirmPlan, setConfirmPlan] = useState<Plan | null>(null);

  useEffect(() => {
    const promises: Promise<any>[] = [
      fetchPlans().then(setPlans).catch(() => setError('Unable to load plans.')),
      fetchAppConfig().then((cfg) => setSignupFreeCredits(cfg.signupFreeCredits)).catch(() => {}),
      fetchComparisonFeatures().then(setComparisonFeatures).catch(() => {}),
    ];
    if (token) {
      promises.push(
        fetchMySubscription().then(setSubscription).catch(() => {}),
        fetchPaymentGateways().then((r) => setGateways(r.gateways || [])).catch(() => {}),
        fetchProrationPreview().then(setProration).catch(() => {}),
      );
    }
    Promise.all(promises).finally(() => setLoading(false));
  }, [token]);

  const loadRazorpay = () => new Promise<boolean>((resolve) => {
    if (window.Razorpay) { resolve(true); return; }
    const script = document.createElement('script');
    script.src = 'https://checkout.razorpay.com/v1/checkout.js';
    script.onload = () => resolve(true);
    script.onerror = () => resolve(false);
    document.body.appendChild(script);
  });

  const buyPlan = async (plan: Plan) => {
    if (plan.slug === 'free') return;
    const preferredGateway = gateways[0]?.id;
    if (!preferredGateway) {
      setMessage('No payment gateway is enabled. Contact support.');
      return;
    }

    setWorkingId(plan.id);
    try {
      const { data: init } = await initiateWithFallback(
        { orderType: 'plan', planId: plan.id },
        preferredGateway,
        gateways.map((g) => g.id),
      );

      if (init.gateway === 'razorpay') {
        const loaded = await loadRazorpay();
        if (!loaded) { setMessage('Unable to load Razorpay checkout.'); return; }
        const razorpay = new window.Razorpay({
          key: init.keyId,
          amount: init.amount,
          currency: init.currency,
          name: 'RideSaathi',
          description: init.description,
          order_id: init.orderId,
          handler: async (response: any) => {
            await verifyPayment({
              gateway: 'razorpay',
              paymentOrderId: init.paymentOrderId,
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_order_id: response.razorpay_order_id,
              razorpay_signature: response.razorpay_signature,
            });
            const updated = await fetchMySubscription().catch(() => null);
            setSubscription(updated);
            setMessage(`${plan.name} plan activated!`);
          },
        });
        razorpay.open();
        return;
      }

      if (init.gateway === 'paytm') {
        localStorage.setItem('paymentReturn', JSON.stringify({ gateway: 'paytm', paymentOrderId: init.paymentOrderId }));
        const form = document.createElement('form');
        form.method = 'POST';
        form.action = `${init.paytmHost}/theia/api/v1/showPaymentPage?mid=${init.mid}&orderId=${init.orderId}`;
        const txnTokenInput = document.createElement('input');
        txnTokenInput.type = 'hidden';
        txnTokenInput.name = 'txnToken';
        txnTokenInput.value = init.txnToken;
        form.appendChild(txnTokenInput);
        document.body.appendChild(form);
        form.submit();
        return;
      }

      if (init.gateway === 'phonepe') {
        localStorage.setItem('paymentReturn', JSON.stringify({ gateway: 'phonepe', paymentOrderId: init.paymentOrderId }));
        window.location.href = init.redirectUrl;
        return;
      }

      if (init.gateway === 'cashfree') {
        await openCashfreeCheckout(init);
        return;
      }

      setMessage('Unsupported payment gateway.');
    } catch (err: any) {
      setMessage(err?.response?.data?.message || 'Unable to start payment. Please try again.');
    } finally {
      setWorkingId(null);
    }
  };

  const popularSlug = 'pro';
  const currentPlanId = subscription?.plan?.id
    || (token ? plans.find((plan) => plan.slug === 'free')?.id : undefined);
  const onPaidPlan = Boolean(subscription && subscription.plan.slug !== 'free');

  // "Upgrade" vs "Downgrade" purely by price relative to the current plan
  const switchVerb = (plan: Plan) => {
    if (!onPaidPlan) return 'Buy';
    return plan.price >= (subscription?.plan.price || 0) ? 'Upgrade to' : 'Downgrade to';
  };

  // Switching from a paid plan gets a confirm dialog (proration summary);
  // buying from Free goes straight to checkout.
  const handlePlanCta = (plan: Plan) => {
    if (onPaidPlan) setConfirmPlan(plan);
    else buyPlan(plan);
  };

  const featureRows: FeatureRow[] = comparisonFeatures.map((cf): FeatureRow => ({
    label: cf.name,
    key: `cf_${cf._id}`,
    format: (_val, plan) =>
      cf.planIds.includes(plan.id)
        ? <CheckCircleIcon color="success" fontSize="small" />
        : <CancelIcon color="disabled" fontSize="small" />,
  }));

  if (loading) {
    return <Box display="flex" justifyContent="center" py={10}><CircularProgress /></Box>;
  }

  if (error || plans.length === 0) {
    return (
      <Stack spacing={3} sx={{ maxWidth: 900, mx: 'auto', px: { xs: 2, md: 4 }, py: 6 }}>
        <Alert severity="error">{error || 'No plans available right now.'}</Alert>
        <Button component={RouterLink} to="/wallet" variant="outlined">Back to wallet</Button>
      </Stack>
    );
  }

  return (
    <Stack spacing={4} sx={{ maxWidth: 1100, mx: 'auto', px: { xs: 2, md: 3 }, py: { xs: 3, md: 5 } }}>
      <Paper
        elevation={0}
        sx={{ p: { xs: 2.5, md: 3 }, bgcolor: '#061b31', color: 'white', background: 'linear-gradient(135deg,#061b31,#0b3156)' }}
      >
        <Typography variant="h4" fontWeight={900}>Choose a plan</Typography>
        <Typography color="rgba(255,255,255,0.72)" mt={0.75}>
          {subscription
            ? subscription.cancelAtPeriodEnd
              ? `Current plan: ${subscription.plan.name} · ends on ${new Date(subscription.endsAt).toLocaleDateString()} (cancellation scheduled)`
              : `Current plan: ${subscription.plan.name} · active until ${new Date(subscription.endsAt).toLocaleDateString()}`
            : token
              ? 'Current plan: Free · upgrade anytime.'
              : 'Pick the right plan for your driving schedule. Upgrade anytime.'}
        </Typography>
      </Paper>

      {/* Launch offer banner */}
      <Paper
        variant="outlined"
        sx={{ p: { xs: 2, md: 2.5 }, borderRadius: 2, borderColor: '#ffc928', bgcolor: '#fffaf0', boxShadow: '0 14px 36px rgba(7,24,45,0.05)' }}
      >
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems={{ xs: 'flex-start', sm: 'center' }} justifyContent="space-between">
          <Box>
            <Chip label="Launch offer" size="small" sx={{ bgcolor: '#ffc928', color: '#07182d', fontWeight: 900, mb: 1 }} />
            <Typography variant="h6" fontWeight={900} color="#07182d">
              First 100 drivers get {signupFreeCredits} free unlocked ride view access.
            </Typography>
            <Typography variant="body2" color="text.secondary" mt={0.5}>
              Early users can view unlocked ride details during the launch offer while slots are available.
            </Typography>
          </Box>
          <Button
            component={RouterLink}
            to={token ? '/rides' : '/signup'}
            variant="contained"
            color="secondary"
            sx={{ flexShrink: 0, width: { xs: '100%', sm: 'auto' } }}
          >
            {token ? 'View rides' : 'Claim access'}
          </Button>
        </Stack>
      </Paper>

      {/* Plan cards */}
      <Grid container spacing={2} justifyContent="center">
        {plans.map((plan) => {
          const isPopular = plan.slug === popularSlug;
          const isCurrent = Boolean(token) && plan.id === currentPlanId;
          const isFree = plan.slug === 'free';

          return (
            <Grid key={plan.id} item xs={12} sm={6} md={4}>
              <Paper
                variant="outlined"
                sx={{
                  p: 2.5,
                  height: '100%',
                  borderColor: isCurrent ? 'success.main' : isPopular ? '#ffc928' : 'rgba(7,24,45,0.1)',
                  borderWidth: isCurrent ? 2 : 1,
                  position: 'relative',
                  boxShadow: isPopular ? '0 22px 58px rgba(7,24,45,0.1)' : 'none',
                }}
              >
                {isCurrent && (
                  <Chip
                    label="Your current plan"
                    size="small"
                    color="success"
                    sx={{ position: 'absolute', top: -13, left: '50%', transform: 'translateX(-50%)', fontWeight: 700 }}
                  />
                )}
                {!isCurrent && isPopular && (
                  <Chip
                    icon={<StarIcon sx={{ fontSize: 14 }} />}
                    label="Most Popular"
                    size="small"
                    sx={{ position: 'absolute', top: -13, left: '50%', transform: 'translateX(-50%)', bgcolor: '#ffc928', color: '#07182d', fontWeight: 700 }}
                  />
                )}
                <Stack spacing={1.5}>
                  <Box>
                    <Typography variant="subtitle2" fontWeight={900} color="text.secondary">{plan.name}</Typography>
                    <Typography variant="h3" fontWeight={900} mt={0.25}>
                      {isFree ? 'Free' : `₹${plan.price}`}
                      {!isFree && <Typography component="span" variant="caption" color="text.secondary"> /mo</Typography>}
                    </Typography>
                    {plan.description && <Typography variant="caption" color="text.secondary">{fixText(plan.description)}</Typography>}
                  </Box>
                  {(plan.features || []).length > 0 ? (
                    <Stack spacing={0.75}>
                      {plan.features.map((feature) => (
                        <Stack key={feature} direction="row" spacing={0.75} alignItems="center">
                          <CheckCircleIcon sx={{ fontSize: 15, color: '#14a35b', flexShrink: 0 }} />
                          <Typography variant="caption">{fixText(feature)}</Typography>
                        </Stack>
                      ))}
                    </Stack>
                  ) : (
                    <Stack spacing={0.75}>
                      <Stack direction="row" spacing={0.75} alignItems="center">
                        <CheckCircleIcon sx={{ fontSize: 15, color: '#14a35b' }} />
                        <Typography variant="caption">
                          {plan.monthlyLeadLimit === null
                            ? 'Unlimited ride unlocks/month'
                            : plan.monthlyLeadLimit === 0
                              ? 'Uses wallet credits'
                              : `${plan.monthlyLeadLimit} ride unlocks/month`}
                        </Typography>
                      </Stack>
                      {plan.topUpAllowed && (
                        <Stack direction="row" spacing={0.75} alignItems="center">
                          <CheckCircleIcon sx={{ fontSize: 15, color: '#14a35b' }} />
                          <Typography variant="caption">Top-up packs available</Typography>
                        </Stack>
                      )}
                      {plan.canExport && (
                        <Stack direction="row" spacing={0.75} alignItems="center">
                          <CheckCircleIcon sx={{ fontSize: 15, color: '#14a35b' }} />
                          <Typography variant="caption">CSV export</Typography>
                        </Stack>
                      )}
                      {plan.prioritySupport && (
                        <Stack direction="row" spacing={0.75} alignItems="center">
                          <CheckCircleIcon sx={{ fontSize: 15, color: '#14a35b' }} />
                          <Typography variant="caption">Priority support</Typography>
                        </Stack>
                      )}
                    </Stack>
                  )}

                  {!token ? (
                    <Button component={RouterLink} to="/signup" variant={isPopular ? 'contained' : 'outlined'} color={isPopular ? 'secondary' : 'primary'} fullWidth>
                      Get started
                    </Button>
                  ) : isCurrent ? (
                    <Button variant="outlined" color="success" fullWidth disabled>
                      Current plan
                    </Button>
                  ) : isFree ? (
                    <Button component={RouterLink} to="/app/rides" variant="outlined" fullWidth>
                      Browse rides
                    </Button>
                  ) : (
                    <>
                      <Button
                        variant={isPopular ? 'contained' : 'outlined'}
                        color={isPopular ? 'secondary' : 'primary'}
                        fullWidth
                        disabled={workingId === plan.id || gateways.length === 0}
                        onClick={() => handlePlanCta(plan)}
                      >
                        {workingId === plan.id
                          ? 'Processing…'
                          : gateways.length === 0
                            ? 'Unavailable'
                            : `${switchVerb(plan)} ${plan.name} — ₹${plan.price}/mo`}
                      </Button>
                      {onPaidPlan && proration?.eligible && (
                        <Typography variant="caption" sx={{ color: '#14a35b', textAlign: 'center', display: 'block' }}>
                          ~{proration.credits} wallet credits back for {proration.unusedDays} unused day(s) of {proration.oldPlanName}
                        </Typography>
                      )}
                    </>
                  )}
                </Stack>
              </Paper>
            </Grid>
          );
        })}
      </Grid>

      {token && gateways.length === 0 && (
        <Alert severity="info">Payment gateways are not configured. Contact support to subscribe.</Alert>
      )}

      {/* Feature comparison table — only shown when admin has added comparison rows */}
      {featureRows.length > 0 && (
        <Paper variant="outlined" sx={{ overflow: 'hidden' }}>
          <Box px={2.5} py={2} borderBottom="1px solid rgba(7,24,45,0.08)">
            <Typography variant="h6" fontWeight={900}>Full feature comparison</Typography>
          </Box>
          <TableContainer sx={{ overflowX: 'auto' }}>
            <Table sx={{ minWidth: 500 }}>
              <TableHead>
                <TableRow sx={{ bgcolor: '#f5f7fb' }}>
                  <TableCell sx={{ fontWeight: 700, width: 200 }}>Feature</TableCell>
                  {plans.map((plan) => (
                    <TableCell key={plan.id} align="center" sx={{ fontWeight: 700 }}>
                      {plan.name}
                      {Boolean(token) && plan.id === currentPlanId && (
                        <Chip label="Current" size="small" color="success" sx={{ ml: 0.75, fontSize: 10, fontWeight: 700 }} />
                      )}
                      {plan.id !== currentPlanId && plan.slug === popularSlug && (
                        <Chip label="Popular" size="small" sx={{ ml: 0.75, bgcolor: '#fff4c7', color: '#9a6a00', fontSize: 10, fontWeight: 700 }} />
                      )}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {featureRows.map((row) => (
                  <TableRow key={row.key} sx={{ '&:last-child td': { border: 0 } }}>
                    <TableCell><Typography variant="body2" fontWeight={600}>{row.label}</Typography></TableCell>
                    {plans.map((plan) => (
                      <TableCell key={plan.id} align="center">
                        {row.format ? row.format((plan as any)[row.key], plan) : String((plan as any)[row.key] ?? '—')}
                      </TableCell>
                    ))}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}

      <Box textAlign="center">
        <Button component={RouterLink} to="/wallet" variant="outlined" size="large">
          Back to wallet
        </Button>
      </Box>

      {/* Confirm switching away from a paid plan */}
      <Dialog open={Boolean(confirmPlan)} onClose={() => setConfirmPlan(null)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 900 }}>
          {confirmPlan ? `${switchVerb(confirmPlan)} ${confirmPlan.name}?` : ''}
        </DialogTitle>
        <DialogContent>
          <Stack spacing={1.5}>
            <Typography variant="body2">
              Your new plan starts today at <b>₹{confirmPlan?.price}/mo</b> and replaces{' '}
              <b>{subscription?.plan.name}</b> immediately.
            </Typography>
            {proration?.eligible ? (
              <Alert severity="success" icon={<CheckCircleIcon fontSize="small" />}>
                Your remaining {proration.unusedDays} day(s) of {proration.oldPlanName} become{' '}
                <b>~{proration.credits} wallet credits</b> after payment.
              </Alert>
            ) : (
              <Typography variant="caption" color="text.secondary">
                No unused-days credit applies to this switch.
              </Typography>
            )}
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setConfirmPlan(null)}>Not now</Button>
          <Button
            variant="contained"
            color="secondary"
            onClick={() => {
              const plan = confirmPlan;
              setConfirmPlan(null);
              if (plan) buyPlan(plan);
            }}
          >
            Continue to payment
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={Boolean(message)}
        autoHideDuration={4000}
        onClose={() => setMessage(null)}
        message={message}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      />
    </Stack>
  );
};

export default Plans;
